import React, {useEffect, useState} from 'react';
import DismissibleAttach, {FileType} from '../../DismissibleAttach/DismissibleAttach';

const $$registeredFilesUniqueIds: Map<File, {
    src: string | null,
    uniqueId: string,
}> = new Map();

const $$clearPreviewsCache = (uniqueIdsToStay: Record<string, string> = {}) => {
    for (const [key, {src, uniqueId}] of $$registeredFilesUniqueIds.entries()) {
        if (uniqueId in uniqueIdsToStay) {
            continue;
        }

        if (src) {
            URL.revokeObjectURL(src);
        }

        $$registeredFilesUniqueIds.delete(key);
    }
};

function toArray<T>(item: T): any[] {
    if (Array.isArray(item)) {
        return item;
    }

    return [item];
}

type SelectedImageMeta = {
    isImage: boolean,
    uniqueId: string,
    meta: FileType,
};

export default function AttachmentsPreviews(props: {
    attachment: File | File[],
    isDisabled?: boolean,
    onDelete?: (index: number) => void,
}) {
    const [previews, setPreviews] = useState<SelectedImageMeta[] | null>(null);

    useEffect(() => $$clearPreviewsCache, []);

    const onDelete = props.onDelete;
    const attachment = props.attachment;

    useEffect(() => {
        const uniqueIdsToStayAlive: Record<string, string> = {};
        const previews = toArray(attachment).map<SelectedImageMeta>((file: File) => {
            const isImage = file.type.split('/').shift() === 'image';

            const cache = $$registeredFilesUniqueIds.get(file);

            const src = isImage ? (cache?.src ?? URL.createObjectURL(file)) : null;
            const uniqueId = cache?.uniqueId ?? (src ?? file.name + '@' + file.size);

            uniqueIdsToStayAlive[uniqueId] = uniqueId;

            if (!cache) {
                $$registeredFilesUniqueIds.set(file, {src, uniqueId});
            }

            return {
                isImage,
                uniqueId,
                meta: {
                    is_image: isImage,
                    file_name: file.name,
                    file_extension: file.name.split('.').pop(),
                    file_mime_type: file.type,
                    asset_file_path: src,
                }
            };
        });

        // clear previews cache
        $$clearPreviewsCache(uniqueIdsToStayAlive);

        setPreviews(previews);
    }, [attachment]);

    return (!previews || !previews.length) ? null : <div className="mt-1 d-flex flex-wrap">
        {previews.map((item, index) => {
            const onDeleteListener = onDelete ? () => onDelete(index) : undefined;

            return <DismissibleAttach
                    key={item.uniqueId + '-' + index}
                    file={item.meta}
                    disabled={props.isDisabled}
                    onDismiss={onDeleteListener}
                    dismissWithoutConfirmation={true}
            />;
        })}
    </div>;
}
